import { useLiveQuery } from "dexie-react-hooks";
import { Link } from "react-router";
import type { HierarchyWarning } from "@/domain/categories/hierarchy";
import { formatDate } from "@/lib/format";
import { COST_NATURE_LABELS, describeCategoryWarning } from "@/lib/labels";
import { Badge, Notice, PageHeader, Panel } from "@/ui/primitives";
import { db, useCategoryTree } from "../hooks";

export function CategoriesPage() {
  const tree = useCategoryTree(true);
  const lastImport = useLiveQuery(() => db.categoryImports.orderBy("importedAt").last(), []);
  if (!tree) return <p className="text-muted">Caricamento…</p>;
  const active = tree.filter((g) => g.group.isActive);
  const count = active.reduce((n, g) => n + g.categories.filter((c) => c.isActive).length, 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Categorie"
        description="Le categorie vengono solo dal foglio Elenco Spese del file Excel, con lo stesso testo. L'app non ne crea né ne modifica."
      />
      <Panel>
        {lastImport ? (
          <p className="text-sm">
            {count} categorie in {active.length} gruppi, importate da <span className="font-medium">{lastImport.source}</span> il{" "}
            {formatDate(lastImport.importedAt.slice(0, 10))}.
          </p>
        ) : (
          <Notice>
            Nessuna categoria. <Link to="/importa" className="font-semibold underline underline-offset-4">Importa il file Excel</Link>.
          </Notice>
        )}
        {lastImport?.warnings.length ? (
          <ul className="mt-2 list-disc pl-5 text-sm text-muted">
            {(lastImport.warnings as (HierarchyWarning | string)[]).map((w, i) => (
              <li key={i}>{describeCategoryWarning(w)}</li>
            ))}
          </ul>
        ) : null}
        <p className="mt-3 text-sm">
          La classificazione fissa / discrezionale si imposta in{" "}
          <Link to="/impostazioni#classificazione" className="font-medium text-petrol underline underline-offset-4">
            Impostazioni
          </Link>
          .
        </p>
      </Panel>

      <div className="columns-1 gap-4 sm:columns-2">
        {tree.map(({ group, categories }) => (
          <section key={group.id} className={`mb-4 break-inside-avoid rounded-xl border border-line bg-surface ${group.isActive ? "" : "opacity-60"}`}>
            <h2 className="border-b border-line px-4 py-2.5 font-semibold text-petrol-strong">
              {group.excelLabel}
              {!group.isActive ? <span className="ml-2 text-sm font-normal text-muted">non più nel file</span> : null}
            </h2>
            <ul className="px-4 py-2 text-[0.95rem]">
              {categories.map((c) => (
                <li key={c.id} className={`flex items-center justify-between gap-2 py-1 ${c.isActive ? "" : "text-muted line-through"}`}>
                  <span>{c.excelLabel}</span>
                  {c.costNature ? <Badge tone={c.costNature === "fixed" ? "petrol" : "neutral"}>{COST_NATURE_LABELS[c.costNature]}</Badge> : null}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
